import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { hashUserId, setUser } from '@siliconx/shared/analytics';
import type { CustomerOrganizationType } from '@siliconx/shared';
import { fetchClientIdentity, getIdToken, getSub, signOut } from './auth';
import { qk } from './queryKeys';

interface AuthUser {
  email: string | null;
  role: 'admin' | 'member' | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  orgType: string | null;
  orgId: string | null;
  orgName: string | null;
  /** @deprecated use canSell */
  hasSellerProfile: boolean;
  /** @deprecated use canBuy */
  hasBuyerProfile: boolean;
  isLoading: boolean;
  logout: () => void;
  // RUF-380: org logo (customer portal only). null when unset.
  logoUrl: string | null;
  // RUF-540: capability flags + missing-field keys, driving nav/route/action gating.
  canSell: boolean;
  canBuy: boolean;
  sellerMissingFields: string[];
  buyerMissingFields: string[];
  // SIL-993: 'business' (default) or 'individual' — drives the profile-
  // completion gate + which profile editor /profile shows.
  organizationType: CustomerOrganizationType | null;
  individualProfileMissing: string[];
  // SIL-1008: true iff the current TOS_VERSION has been accepted — drives
  // TosGate (__root.tsx). Defaults to false (fail closed) while loading.
  tosAccepted: boolean;
  // SIL-994: portal-wide identity label (business legalName, individual
  // firstName+lastName) — see ClientIdentity.displayName's docstring.
  displayName: string | null;
  // SIL-1132: see ClientIdentity.hasAssets's docstring.
  hasAssets: boolean;
  // 929: own-org nudge counts for one-time manual serial entry (see ClientIdentity).
  serialEntryPendingCount: number;
  serialEntryServerCount: number;
  serialEntryFirstAssetId: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Org identity (type/id/role) is DB-derived — fetched from /api/client/me,
  // not decoded from JWT claims (the client pool has no custom:org* attributes).
  //
  // Shared ['client-identity'] query key with the dashboard/onboarding routes:
  // AuthProvider mounts during /auth/callback, before exchangeCodeForTokens
  // writes the ID token to sessionStorage, so this fetch can fail on first
  // mount. React-query's default retry survives that window, and because the
  // key is shared, any consumer's successful fetch (e.g. the dashboard's)
  // re-renders this context too — no hard refresh needed.
  const { data, isPending, error } = useQuery({
    queryKey: qk.identity(),
    queryFn: fetchClientIdentity,
  });

  const identity = data ?? null;

  // SIL-1529: opaque GA4 user_id + org-level user properties. No PII.
  useEffect(() => {
    if (!identity) return;
    const idToken = getIdToken();
    const sub = idToken ? getSub(idToken) : null;
    if (!sub) return;
    let cancelled = false;
    void hashUserId(sub).then((userId) => {
      if (cancelled) return;
      setUser(userId, {
        organization_type: identity.organizationType ?? 'unknown',
        org_type: identity.orgType ?? 'unknown',
        can_buy: identity.canBuy ? 'true' : 'false',
        can_sell: identity.canSell ? 'true' : 'false',
      });
    });
    return () => {
      cancelled = true;
    };
  }, [identity]);

  // 2026-09-21 redirect loop: every route under this provider already passed
  // beforeLoad's silentRefreshOrRedirect(), so the token is live - a 401 here
  // means Cognito knows this user but there is no client_user row (an
  // unfinished signup: the Cognito user is created at POST /api/pub/signup,
  // the client_user row only at /confirm). Falling through rendered the app
  // with `tosAccepted: false`, TosGate redirected to /tos, and /tos's
  // "Sign in" link led straight back here. Signing in again cannot fix this
  // state, so stop and offer the one action that can: sign out.
  if ((error as { status?: number } | null)?.status === 401) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
        <h1 className="text-lg font-semibold text-foreground">No portal account for this sign-in</h1>
        <p className="max-w-md text-sm text-muted-foreground">
          You are signed in, but this login is not linked to a SiliconX portal account yet. If you
          were creating an account, sign out, open your invitation link again, and finish the email
          verification step. Otherwise contact{' '}
          <a href="mailto:support@siliconx.com" className="text-brand-600 underline">
            support@siliconx.com
          </a>
          .
        </p>
        <button
          type="button"
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          onClick={signOut}
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user: identity ? { email: identity.email, role: identity.orgRole } : null,
        orgType: identity?.orgType ?? null,
        orgId: identity?.orgId ?? null,
        orgName: identity?.orgName ?? null,
        hasSellerProfile: identity?.hasSellerProfile ?? false,
        hasBuyerProfile: identity?.hasBuyerProfile ?? false,
        isLoading: isPending,
        logout: signOut,
        logoUrl: identity?.logoUrl ?? null,
        canSell: identity?.canSell ?? false,
        canBuy: identity?.canBuy ?? false,
        sellerMissingFields: identity?.sellerMissingFields ?? [],
        buyerMissingFields: identity?.buyerMissingFields ?? [],
        organizationType: identity?.organizationType ?? null,
        individualProfileMissing: identity?.individualProfileMissing ?? [],
        displayName: identity?.displayName ?? null,
        tosAccepted: identity?.tosAccepted ?? false,
        hasAssets: identity?.hasAssets ?? false,
        serialEntryPendingCount: identity?.serialEntryPendingCount ?? 0,
        serialEntryServerCount: identity?.serialEntryServerCount ?? 0,
        serialEntryFirstAssetId: identity?.serialEntryFirstAssetId ?? null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
